import React from 'react';
import PropTypes from 'prop-types';

import { NoData, InfiniteScroll, EndlessScroll } from '@meronex/components';
import { App } from '@meronex/app';

import StoreItem from '../storeItems/StoreItem';
import ItemInDialog from '../storeItems/actions/ItemInDialog';
import ItemOutDialog from '../storeItems/actions/ItemOutDialog';
import ItemTransferDialog from './actions/ItemTransferDialog';
import AuditItemDialog from '../storeItems/actions/AuditItemDialog';
import ActionsDialog from '../storeItems/actions/ActionsDialog';

import { useWindowSize } from '@react-hook/window-size';
import { useTheme } from '@material-ui/core/styles';

import { storesMng } from '../../api/storesMng';

export default function StoreItemsList(props) {
  const {
    store,
    storeDrawerOpen,
    onAddStoreItem,
    onRemoveStoreItem,
    onRestoreStoreItem,
    onArchiveStoreItem,
    onModifyStoreItem,
    onReverseTransaction,
    onItemTransfer,
    onItemIn,
    onItemOut,
    onItemClick,
    viewOnly,
    permissions,
    filters,
    onItemsLoaded,
  } = props;


  const [openItemInDialog, setOpenItemInDialog] = React.useState(false);
  const [openItemOutDialog, setOpenOutDialog] = React.useState(false);
  const [openTransferDialog, setOpenTransferDialog] = React.useState(false);
  const [openAuditDialog, setOpenAuditDialog] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState();
  const [selectedItemIndex, setSelectedItemIndex] = React.useState();
  const [selectedItemProducts, setSelectedItemProducts] = React.useState();
  const infiniteScrollRef = React.useRef(null);
  const filterTimeoutRef = React.useRef(null);
  const skipRef = React.useRef(-1);
  const [width, height] = useWindowSize();
  const [openActionsDialog, setOpenActionsDialog] = React.useState();

  const getStoreItemProducts = async () => {
    if (selectedItem && selectedItem._id) {
      const result = await storesMng.getStoreItemProducts(selectedItem._id);
      setSelectedItemProducts(result);
    }
  };

  React.useEffect(() => {
    App.eventsManager.on('event-active-view', StoreItemsList.name, (e) => {
      if (e.activeView === 'stores') {
        return refreshList();
      }
    });
    return () => {
      App.eventsManager.removeListener('event-active-view', StoreItemsList.name);
    };
  }, []);

  React.useEffect(() => {
    clearTimeout(filterTimeoutRef.current);
    filterTimeoutRef.current = setTimeout(() => {
      refreshList();
    }, 450);
    return () => clearTimeout(filterTimeoutRef.current);
  }, [filters, store]);

  React.useEffect(() => {
    if (selectedItem) {
      getStoreItemProducts();
    }
  }, [selectedItem]);

  const refreshItems = () => {
    if (infiniteScrollRef.current) {
      infiniteScrollRef.current.refreshItems();
    }
  };

  const refreshSelectedItem = (scrollToTop, clearItems) => {
    // note that scrollToTop and clearItems are kept for backward
    // compatability with infinite list
    const { current } = infiniteScrollRef;

    if (current && typeof current.refreshSelectedItem) {
      infiniteScrollRef.current.refreshSelectedItem(selectedItemIndex);
    } else refreshList(scrollToTop, clearItems);
  };

  const refreshList = (scrollToTop, clearItems) => {
    skipRef.current = -1;
    if (infiniteScrollRef.current) {
      infiniteScrollRef.current.refreshList(scrollToTop, clearItems);
    }
  };

  const renderItem = (item, index) => {
    if (!item || !item.type) {
      return null;
    }
    return (
      <StoreItem
        key={item._id}
        item={item}
        permissions={permissions}
        itemProducts={selectedItemProducts}
        showItemDetails={false}
        onClick={() => {
          if (!viewOnly) {
            setSelectedItem(item);
            setSelectedItemIndex(index);
            setOpenActionsDialog(true);
            onItemClick(item);
          }
        }}
        onItemIn={() => {
          setOpenItemInDialog(true);
          setSelectedItem(item);
        }}
        onItemOut={() => {
          setOpenOutDialog(true);
          setSelectedItem(item);
        }}
        onItemTransfer={() => {
          setOpenTransferDialog(true);
          setSelectedItem(item);
        }}
        onItemAudit={() => {
          setOpenAuditDialog(true);
          setSelectedItem(item);
        }}
        onRemoveStoreItem={onRemoveStoreItem}
        onRestoreStoreItem={onRestoreStoreItem}
        showBatches={false}
        // last={index === storeItems.length - 1}
        onToggleActive={() => {
          refreshSelectedItem();
        }}
      />
    );
  };
  const getItemHeight = (item) => {
    let height = 110;
    if (item) {
      if (item.type && item.type.price) {
        height += 22;
      }
    }

    return height;
  };

  const fetchStoreItem = async (itemId) => {
    return await storesMng.getStoreItem(itemId);
  };


  const fetchStoreItems = async ({ limit, skip }) => {
    let _storeItems;

    // this is added to prevent the bug
    // when filtering and while having scrolled down
    // causing a flicker due to infinite loop
    if (skip === 0 || skip > skipRef.current) {
      skipRef.current = skip;
      try {
        _storeItems = await storesMng.getStoreItemsCursor({
          storeId: store._id,
          skip,
          limit,
          filters,
        });
        
        // Always ensure we have a valid array
        if (!_storeItems || !Array.isArray(_storeItems)) {
          _storeItems = [];
        }
        
        // Filter out any null/undefined items that might cause issues
        _storeItems = _storeItems.filter(item => item && item._id);

        // Signal that items have loaded (first page)
        if (skip === 0 && onItemsLoaded) {
          onItemsLoaded();
        }

        // If no items and skip > 0, return empty array to signal end of data
        if (_storeItems.length === 0 && skip > 0) {
          return [];
        }
        
      } catch (error) {
        console.error('Error fetching store items:', error);
        _storeItems = [];
      }
    }
    
    // Always return an array, never null
    return Array.isArray(_storeItems) ? _storeItems : [];
  };

  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const getHeight = () => {
    let isMobile = App.utils.isMobile();
    // mobile: app header(56) + view header(68) + stat card(~110) + search(~70) + padding(~40)
    // desktop: sidebar takes no vertical space, view header(68) + stat card(~110) + search(~70) + padding(~72)
    let fixedOffset = isMobile ? 350 : 320;
    return height - fixedOffset;
  };

  const renderListSkeleton = () => {
    const skFrom = isDark ? '#2a2a2e' : '#e2e8f0';
    const skTo = isDark ? '#3a3a40' : '#f1f5f9';
    const Sk = ({ w, h, r = 8, style = {}, delay = 0 }) => (
      <div style={{
        width: w, height: h, borderRadius: r,
        backgroundColor: skFrom,
        animation: `skItemPulse 2s ease-in-out ${delay}s infinite`,
        ...style,
      }} />
    );
    const listHeight = getHeight();
    const rowHeight = 100;
    const rowCount = Math.max(Math.floor(listHeight / rowHeight), 4);
    return (
      <div style={{ height: listHeight, overflow: 'hidden' }}>
        <style>{`
          @keyframes skItemPulse {
            0%, 100% { background-color: ${skFrom}; }
            50% { background-color: ${skTo}; }
          }
        `}</style>
        {Array.from({ length: rowCount }, (_, i) => i * 0.06).map((d, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '14px 16px',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#f1f5f9'}`,
          }}>
            <Sk w="70px" h="70px" r={12} delay={d} />
            <div style={{ flex: 1 }}>
              <Sk w="55%" h="14px" r={4} delay={d + 0.03} />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Sk w="60px" h="10px" r={3} delay={d + 0.06} />
                <Sk w="40px" h="10px" r={3} delay={d + 0.08} />
                <Sk w="50px" h="10px" r={3} delay={d + 0.1} />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Sk w="50px" h="18px" r={4} delay={d + 0.05} style={{ marginLeft: 'auto' }} />
              <Sk w="30px" h="10px" r={3} delay={d + 0.08} style={{ marginTop: 4, marginLeft: 'auto' }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div key={store._id} style={{
        backgroundColor: isDark ? '#2a2a2e' : '#fff',
        borderRadius: '16px',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
        margin: '8px 20px',
        overflow: 'hidden',
      }}>
        <EndlessScroll
          limit={15} // make sure limit is large enough to fill the screen height
          ref={infiniteScrollRef}
          loadingRenderer={renderListSkeleton}
          scrollRenderer={(items, startIndex, stopIndex) => {
            const item = items[stopIndex];
            if (item && item.type && item.type.name) {
              return (
                <div style={{
                  position: 'fixed',
                  bottom: '40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 100,
                  pointerEvents: 'none',
                  backgroundColor: 'rgba(15, 23, 42, 0.8)',
                  backdropFilter: 'blur(4px)',
                  color: '#fff',
                  borderRadius: '12px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  maxWidth: '280px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {stopIndex + 1} — {item.type.name}
                </div>
              );
            }
            return <span />;
          }}
          height={getHeight()}
          remoteRowCount={1000}
          selectedItemIndex={selectedItemIndex}
          listStyle={{
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
          renderItem={(data, isScrolling, index) => {
            // Additional safety check to prevent null items from being rendered
            if (!data || !data._id) {
              return null;
            }
            return renderItem(data, index);
          }}
          itemHeight={(item) => {
            // Safety check to prevent crashes with null items
            if (!item || !item._id) {
              return 120; // Default height
            }
            return getItemHeight(item);
          }}
          lastItemHeight={(item) => {
            // Safety check to prevent crashes with null items
            if (!item || !item._id) {
              return 255; // Default height + 135
            }
            return getItemHeight(item) + 135;
          }}
          noRowRenderer={() => {
            return (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
              }}>
                <div
                  onClick={() => onAddStoreItem()}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '16px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.25)' : '#e0e7ff';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontSize: '24px', color: '#6366f1', fontWeight: 300 }}>+</span>
                </div>
                <div style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: isDark ? '#e2e8f0' : '#0f172a',
                  marginBottom: '4px',
                }}>
                  No items found
                </div>
                <div
                  onClick={() => onAddStoreItem()}
                  style={{
                    fontSize: '13px',
                    color: '#6366f1',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  Add item
                </div>
              </div>
            );
          }}
          getListItem={fetchStoreItem}
          getListItems={fetchStoreItems}
        />
      </div>
      {!viewOnly && selectedItem && (
        <React.Fragment>
          <ItemInDialog
            open={openItemInDialog}
            item={selectedItem}
            onClose={() => {
              setOpenItemInDialog(false);
            }}
            onItemIn={(cb) => {
              if (onItemIn) {
                onItemIn(cb);
              } else {
                cb();
              }
              refreshSelectedItem(true, true);
            }}
          />
          <ItemOutDialog
            open={openItemOutDialog}
            item={selectedItem}
            onItemOut={() => {
              if (onItemOut) {
                onItemOut();
              }
              refreshSelectedItem(true, true);
            }}
            onClose={() => {
              setOpenOutDialog(false);
            }}
          />
          <ItemTransferDialog
            open={openTransferDialog}
            item={selectedItem}
            onItemTransfer={() => {
              if (onItemTransfer) {
                onItemTransfer();
              }
              refreshSelectedItem(true, true);
            }}
            onClose={() => {
              setOpenTransferDialog(false);
            }}
          />
          {openAuditDialog && (
            <AuditItemDialog
              open={openAuditDialog}
              item={selectedItem}
              itemProducts={selectedItemProducts}
              permissions={permissions}
              onReverseTransaction={() => {
                refreshList(true, true);
                if (onReverseTransaction) {
                  onReverseTransaction();
                }
              }}
              onClose={() => {
                setOpenAuditDialog(false);
              }}
            />
          )}
          {openActionsDialog && selectedItem && (
            <ActionsDialog
              permissions={permissions}
              item={selectedItem}
              itemProducts={selectedItemProducts}
              open={openActionsDialog}
              onClose={() => {
                setOpenActionsDialog(false);
              }}
              onItemIn={() => {
                setOpenItemInDialog(true);
                setOpenActionsDialog(false);
              }}
              onItemOut={() => {
                setOpenOutDialog(true);
                setOpenActionsDialog(false);
              }}
              onItemRestore={onRestoreStoreItem}
              onItemTransfer={() => {
                setOpenTransferDialog(true);
                setOpenActionsDialog(false);
              }}
              onItemManage={() => {
                setOpenAuditDialog(true);
                setOpenActionsDialog(false);
              }}
              onItemDelete={onRemoveStoreItem}
              onToggleActive={refreshSelectedItem}
              onModifyStoreItem={() => {
                refreshList(true, true);
                if (onModifyStoreItem) {
                  onModifyStoreItem();
                }
              }}
              onArchiveStoreItem={() => {
                refreshList(true, true);
                setOpenActionsDialog(false);
                if (onArchiveStoreItem) {
                  onArchiveStoreItem();
                }
              }}
              onAdjustUnitRate={() => {
                refreshList(true, true);
              }}
            />
          )}
        </React.Fragment>
      )}
    </div>
  );
}

StoreItemsList.propTypes = {
  store: PropTypes.object,
  onItemClick: PropTypes.func,
  storeDrawerOpen: PropTypes.bool,
  onExpandItem: PropTypes.func,
  onAddStoreItem: PropTypes.func,
  onAddStoreProduct: PropTypes.func,
  onModifyStoreItem: PropTypes.func,
  onRestoreStoreItem: PropTypes.func,
  onArchiveStoreItem: PropTypes.func,
  onReverseTransaction: PropTypes.func,
  onItemIn: PropTypes.func,
  onItemOut: PropTypes.func,
  onItemTransfer: PropTypes.func,
  viewOnly: PropTypes.bool,
  permissions: PropTypes.object,
  filters: PropTypes.object,
};
